import * as moment from 'moment'
import React from 'react'
import Navigator from '../app/navigator'
import { ErrorGroupIncident, ErrorGroupState } from '../domain/Error'
import { incidentIcon, IncidentType, isAnyFailureRateIncident } from '../domain/Incident'
import Duration from '../helper/Duration'
import Link from '../helper/link'
import MomentDate from '../momentDate'
import { Box, BoxBody, BoxHeader, BoxTitle } from '../ui/box'
import { IssueWorkflowButtonGroup } from './details/IssueWorkflowButtonGroup'
import StatusLabel from './details/statusLabel'
import { ErrorListItemComponent } from './ErrorListItem'
import ChangedTransactionsList from '../monitor/events/changedTransactionsList'
import { SnapshotComparison } from '../domain/SnapshotComparison'
import { TidewaysContext } from '../app/context'
import { ActionExecution } from '../domain/ActionExecution'
import { NotificationLog } from './details/NotificationLog'
import SnapshotComparisonGraph from '../monitor/events/snapshotComparisonGraph'
import { TidewaysEvent } from '../domain/Event'
import Tooltip from '../monitor/graph/tooltip'

type DetailsTabType = 'values' | 'transactions' | 'notifications'

interface Props {
    errorGroup: ErrorGroupIncident
    relatedAlertActionExecutions?: ActionExecution[]
    snapshotComparison: SnapshotComparison | undefined
    tidewaysContext: TidewaysContext
    navigator: Navigator
    loading: boolean
    parameters: {
        offset: string
        page: string
        error: string
        reload: string
    }
    events?: TidewaysEvent[]
}

interface State {
    activeDetailsView: DetailsTabType
    activeSlot: {
        timeIndex: any
        mouse: any
    }
}

export default class IncidentDetails extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props)

        this.state = {
            activeDetailsView: this.hasChangedTransactions() ? 'transactions' : 'values',
            activeSlot: {
                timeIndex: null,
                mouse: null,
            },
        }
    }

    public render() {
        const { errorGroup, snapshotComparison, relatedAlertActionExecutions } = this.props
        const { incident } = errorGroup
        const severityClass = { critical: 'red', warning: 'yellow', normal: 'green' }
        const severityIcon = { critical: 'bolt', warning: 'bell', normal: 'check' }

        const failureThresholdLabels = {
            none: 'None',
            'may-never-fail': 'May Never Fail',
            'one-percent': '1%',
            'five-percent': '5%',
            'ten-percent': '10%',
            'twenty-five-percent': '25%',
            'fifty-percent': '50%',
        }

        const comparisonReference = this.props.tidewaysContext.application.license.traceAggregation
            ? {
                  type: 'incident',
                  referenceId: incident.id.toString(),
              }
            : null

        const router = this.props.tidewaysContext.router
        const showCharts = () => {
            switch (errorGroup.status) {
                case ErrorGroupState.ALWAYS_IGNORED:
                case ErrorGroupState.IGNORED:
                case ErrorGroupState.RESOLVED:
                    return false
                default:
                    return true
            }
        }

        const isHeartbeat = incident.type === IncidentType.MISSING_DATA

        return incident ? (
            <div>
                <Box className="error-item-detail jquery-tooltip-snapshot-comparison-graph">
                    <BoxHeader flexbox>
                        <div className="text-mute tw-font-size-24 tw-pl-16">
                            <i className={'fa ' + incidentIcon(errorGroup.incident.type)} />
                        </div>
                        <BoxTitle>Incident: {errorGroup.type}</BoxTitle>
                        <div className="box-tools pull-right" style={{ marginLeft: 'auto' }}>
                            <StatusLabel status={errorGroup.status} />
                        </div>
                    </BoxHeader>
                    <BoxBody>
                        <div className="row">
                            <div className="col-md-8">
                                <dl className="dl-horizontal">
                                    <div>
                                        <dt>Start</dt>
                                        <dd>
                                            <MomentDate date={errorGroup.incident.startDate} />
                                        </dd>

                                        <dt>End</dt>
                                        <dd>
                                            {errorGroup.incident.endDate ? (
                                                <MomentDate date={errorGroup.incident.endDate} />
                                            ) : (
                                                'still ongoing..'
                                            )}
                                        </dd>
                                        <dt>Duration</dt>
                                        <dd>
                                            {errorGroup.lastOccured.date === '' ? 'ongoing for ' : null}
                                            <Duration value={incident.duration} />
                                        </dd>
                                        {!isHeartbeat && (
                                            <>
                                                <dt>Last Value</dt>
                                                <dd>{errorGroup.incident.lastValue}</dd>
                                                <dt>Threshold</dt>
                                                <dd>{errorGroup.incident.threshold}</dd>
                                            </>
                                        )}
                                        <dt>Environment</dt>
                                        <dd>{errorGroup.environments}</dd>

                                        <dt>Service</dt>
                                        <dd>{errorGroup.services}</dd>

                                        {incident.transaction && (
                                            <>
                                                <dt>Transaction</dt>
                                                <dd>
                                                    <Link
                                                        route={'XhprofProfilerBundle.Transaction.show'}
                                                        params={{
                                                            operation: incident.transaction.id,
                                                            s: incident.service,
                                                            env: incident.environment,
                                                        }}
                                                    >
                                                        {incident.transaction.identifier}
                                                    </Link>
                                                </dd>
                                            </>
                                        )}
                                        {incident.transaction && incident.type === IncidentType.TRANSACTION_FAILURE_RATE && (
                                            <div>
                                                <dt>Failure Threshold</dt>
                                                <dd>
                                                    {failureThresholdLabels[incident.transaction.failureThreshold]} (
                                                    <Link
                                                        route={'XhprofProfilerBundle.Transaction.edit'}
                                                        skipHistoryApi={true}
                                                        params={{
                                                            operation: incident.transaction.id,
                                                            s: incident.service,
                                                            env: incident.environment,
                                                        }}
                                                    >
                                                        Change
                                                    </Link>
                                                    )
                                                </dd>
                                            </div>
                                        )}
                                        {incident.transaction && isHeartbeat && (
                                            <>
                                                <dt>Check-In Interval</dt>
                                                <dd>
                                                    {incident.transaction.alertAfterInterval ?? '-'} (
                                                    <Link
                                                        route={'XhprofProfilerBundle.Transaction.edit'}
                                                        skipHistoryApi={true}
                                                        params={{
                                                            operation: incident.transaction.id,
                                                            s: incident.service,
                                                            env: incident.environment,
                                                        }}
                                                    >
                                                        Change
                                                    </Link>
                                                    )
                                                </dd>
                                                <dt>Last Recorded</dt>
                                                <dd>
                                                    <MomentDate date={incident.transaction.lastRecorded} />
                                                </dd>
                                                <dt>Last Successful</dt>
                                                <dd>
                                                    <MomentDate date={incident.transaction.lastSuccessfulAt} />
                                                </dd>
                                            </>
                                        )}
                                    </div>
                                </dl>
                            </div>
                            <div className="col-md-4 text-right">
                                <IssueWorkflowButtonGroup
                                    errorGroup={this.props.errorGroup}
                                    updateErrorState={this.updateErrorState}
                                    loading={this.props.loading}
                                    navigator={this.props.navigator}
                                    acknowledgeFeatureActive={false}
                                    isElevated={this.props.tidewaysContext.isElevated}
                                />
                            </div>
                        </div>
                        <div className="row snapshotComparison">
                            {snapshotComparison && this.props.events && showCharts() && (
                                <>
                                    <Tooltip
                                        data={{ report: snapshotComparison.reportMerged }}
                                        activeSlot={this.state.activeSlot}
                                        percentileLabel="95% Percentile"
                                        downstreamEnabled={snapshotComparison.reportMerged.hasDownstream}
                                        showAverages={false}
                                        parentSelector=".jquery-tooltip-snapshot-comparison-graph"
                                        moveUp={20}
                                    />
                                    <div className={'col-lg-12'}>
                                        <SnapshotComparisonGraph
                                            router={router}
                                            reportMerged={snapshotComparison.reportMerged}
                                            events={this.props.events}
                                            changeActiveSlot={this.changeActiveSlot}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </BoxBody>
                </Box>

                {this.renderRelatedErrors(errorGroup)}

                <div className="row">
                    <div className="col-sm-12">
                        <ul className="nav nav-pills nav-pills-tideways">
                            {this.hasChangedTransactions() ? (
                                <li className={this.state.activeDetailsView === 'transactions' ? 'active' : ''}>
                                    <a
                                        href="#transactions"
                                        data-target="#transactions"
                                        data-toggle="tab"
                                        onClick={() => this.setState({ activeDetailsView: 'transactions' })}
                                    >
                                        Transactions
                                    </a>
                                </li>
                            ) : null}
                            {incident.type === IncidentType.MISSING_DATA ? null : (
                                <li className={this.state.activeDetailsView === 'values' ? 'active' : ''}>
                                    <a
                                        href="#values"
                                        data-target="#values"
                                        data-toggle="tab"
                                        onClick={() => this.setState({ activeDetailsView: 'values' })}
                                    >
                                        Values
                                    </a>
                                </li>
                            )}
                            <li className={this.state.activeDetailsView === 'notifications' ? 'active' : ''}>
                                <a
                                    href="#notificationLog"
                                    data-target="#notificationLog"
                                    data-toggle="tab"
                                    onClick={() => this.setState({ activeDetailsView: 'notifications' })}
                                >
                                    Notification Logs
                                </a>
                            </li>
                        </ul>

                        <div className="tab-content no-padding">
                            {snapshotComparison && this.hasChangedTransactions() ? (
                                <div
                                    className={`tab-pane ${this.state.activeDetailsView === 'transactions' ? 'active' : ''}`}
                                    id="transactions"
                                >
                                    <ChangedTransactionsList
                                        transactions={snapshotComparison.degradedTransactions}
                                        type={'degraded'}
                                        isTideways6={this.props.tidewaysContext.organization.isTideways6}
                                        comparisonReference={comparisonReference}
                                    />
                                    <ChangedTransactionsList
                                        transactions={snapshotComparison.improvedTransactions}
                                        type={'improved'}
                                        isTideways6={this.props.tidewaysContext.organization.isTideways6}
                                        comparisonReference={comparisonReference}
                                    />
                                </div>
                            ) : null}
                            {incident.type === IncidentType.MISSING_DATA ? null : (
                                <div className={`tab-pane ${this.state.activeDetailsView === 'values' ? 'active' : ''}`} id="values">
                                    <div className="box">
                                        <div className="box-header">
                                            <h3 className="box-title">Expected Thresholds and Observed Data</h3>
                                        </div>

                                        <table className="table table-striped">
                                            <tbody>
                                                <tr>
                                                    <th style={{ width: 50 }}></th>
                                                    <th>Date</th>
                                                    <th>Threshold</th>
                                                    <th>{this.renderObservedDataLabel(errorGroup)}</th>
                                                </tr>
                                                {incident.alerts.map((alert, key) => (
                                                    <tr key={key}>
                                                        <td>
                                                            <small className={'badge pull-right bg-' + severityClass[alert.severity]}>
                                                                <i className={'fa fa-' + severityIcon[alert.severity]} />
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <MomentDate date={moment.unix(Number(alert.date)).utc()} />
                                                        </td>
                                                        <td>{alert.threshold}</td>
                                                        <td>{alert.value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            <div
                                className={`tab-pane ${this.state.activeDetailsView === 'notifications' ? 'active' : ''}`}
                                id="notificationLog"
                            >
                                <Box>
                                    <BoxBody noPadding>
                                        <NotificationLog actionExecutions={relatedAlertActionExecutions}></NotificationLog>
                                    </BoxBody>
                                </Box>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ) : null
    }

    private hasChangedTransactions(): boolean {
        const { snapshotComparison } = this.props

        if (!snapshotComparison) {
            return false
        }

        return snapshotComparison.improvedTransactions.length + snapshotComparison.degradedTransactions.length > 0
    }

    private renderObservedDataLabel(errorGroup: ErrorGroupIncident) {
        if (errorGroup.incident.type === IncidentType.RESPONSE_TIME) {
            return 'Response Time (in ms)'
        }
        if (errorGroup.incident.type === IncidentType.ERROR_RATE) {
            return 'Failure Rate (in %)'
        }
        if (errorGroup.incident.type === IncidentType.TRANSACTION_FAILURE_RATE) {
            return 'Failure Rate (in %)'
        }
        if (errorGroup.incident.type === IncidentType.TRANSACTION_RESPONSE_TIME) {
            return 'Above Threshold (in %)'
        }
        return errorGroup.exceptionType
    }

    private changeActiveSlot = activeSlot => this.setState({ activeSlot })

    private renderRelatedErrors(errorGroup: ErrorGroupIncident) {
        if (!isAnyFailureRateIncident(errorGroup.incident)) {
            return null
        }

        // we do not want to confuse users with an empty list, suggesting that we do not know the cause
        // in lots of cases. There might be high failure rates with no related errors (500s with properly catched exceptions)
        if (!errorGroup.relatedErrors?.errors.length) {
            return null
        }

        return (
            <Box>
                <BoxHeader>
                    <BoxTitle>Related Errors</BoxTitle>
                </BoxHeader>
                <BoxBody className="no-padding">
                    {errorGroup.relatedErrors?.errors.map(error => (
                        <ErrorListItemComponent
                            key={error.id}
                            error={error}
                            currentPage={1}
                            currentRoute={'XhprofErrorBundle.Issues.errors'}
                            parameters={{}}
                        />
                    ))}
                    {!errorGroup.relatedErrors?.errors.length && (
                        <div className="tw-p-12">
                            <p>No related errors or exceptions found. Failures can be recorded in two other ways:</p>

                            <ol>
                                <li>
                                    Requests that are slower than their{' '}
                                    <a href="https://support.tideways.com/documentation/setup/configuration/response-time-targets.html?utm_medium=app&utm_source=incident-details&utm_campaign=response-time-targets">
                                        configured response time targets.
                                    </a>
                                </li>
                                <li>Requests resulting in a 5xx status code or CLI scripts with an exit code unequal to 0.</li>
                            </ol>
                        </div>
                    )}
                </BoxBody>
            </Box>
        )
    }

    private updateErrorState = href => {
        jQuery.ajax({
            url: href,
            success: data => {
                // we need to force a reload, otherwise no new ajax call is made.
                const parameters = {
                    ...this.props.parameters,
                    reload: (parseInt(this.props.parameters.reload, 0) || 0) + 1,
                }

                this.props.navigator.replace(parameters)
            },
        })
    }
}
